// src/pages/Attendance/EmployeeAttendancePage.js
// One employee's month: calendar, selected-day details, summary ring.
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, ArrowLeft } from 'lucide-react';
import { fetchMonthlyAttendance } from '../../services/features/attendance/attendanceSlice';
import {
  WEEKDAYS,
  STATUS_META,
  statusMeta,
  addMonths,
  formatDateKey,
  formatDuration,
  formatFullDate,
  formatISTTime,
  monthLabel,
} from '../../utils/attendanceFormat';
import '../Shared/ReportPages.css';

const coord = (loc) =>
  loc && typeof loc.latitude === 'number'
    ? { text: `${loc.latitude.toFixed(4)}, ${loc.longitude.toFixed(4)}`, lat: loc.latitude, lng: loc.longitude }
    : null;

const Ring = ({ value }) => {
  const size = 120;
  const r = 46;
  const c = 2 * Math.PI * r;
  const dash = (Math.min(100, Number(value) || 0) / 100) * c;
  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <circle cx="60" cy="60" r={r} fill="none" stroke="var(--border)" strokeWidth="12" />
      <circle
        cx="60" cy="60" r={r} fill="none" stroke="#10b981" strokeWidth="12" strokeLinecap="round"
        strokeDasharray={`${dash} ${c - dash}`} transform="rotate(-90 60 60)"
      />
      <text x="60" y="65" textAnchor="middle" fontSize="18" fontWeight="800" fill="var(--text-primary)">
        {Number(value || 0).toFixed(2)}%
      </text>
    </svg>
  );
};

const EmployeeAttendancePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const [sp] = useSearchParams();
  const { monthly, monthlyLoading } = useSelector((s) => s.attendance);

  const todayKey = formatDateKey();
  const currentMonth = todayKey.slice(0, 7);
  const empId = employeeId === 'me' ? undefined : employeeId;

  const [month, setMonth] = useState(sp.get('month') || currentMonth);
  const [selected, setSelected] = useState(sp.get('date') || todayKey);

  useEffect(() => {
    dispatch(fetchMonthlyAttendance({ employeeId: empId, month }));
  }, [dispatch, empId, month]);

  // Only trust data that belongs to the month on screen
  const data = monthly?.month === month ? monthly : null;

  const byDate = useMemo(() => {
    const m = {};
    (data?.items || []).forEach((it) => (m[it.date] = it));
    return m;
  }, [data]);

  const cells = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    const first = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
    const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const arr = Array.from({ length: first }, () => null);
    for (let d = 1; d <= days; d++) {
      const key = `${month}-${String(d).padStart(2, '0')}`;
      arr.push({ day: d, date: key, record: byDate[key] });
    }
    return arr;
  }, [month, byDate]);

  const summary = data?.summary || {};
  const pct = Number(data?.attendancePct) || 0;

  const changeMonth = (delta) => {
    setMonth((m) => addMonths(m, delta));
    setSelected(null);
  };

  const inMonth = selected && selected.startsWith(month);
  const rec = inMonth ? byDate[selected] : null;
  const recMeta = rec ? statusMeta(rec.status) : null;
  const inLoc = rec ? coord(rec.checkInLocation) : null;
  const outLoc = rec ? coord(rec.checkOutLocation) : null;

  const name = sp.get('name') || 'Employee';
  const meta = [sp.get('role'), sp.get('mobile')].filter(Boolean).join(' · ');

  return (
    <div className="rp-page">
      <div className="rp-toolbar">
        <button className="rp-chip" onClick={() => navigate('/attendance')}>
          <ArrowLeft size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
          Attendance Dashboard
        </button>
      </div>

      {/* Employee header */}
      {empId && (
        <div className="rp-card rp-card-pad">
          <div className="rp-person" style={{ gap: 14 }}>
            <div className="rp-avatar lg">{name.charAt(0).toUpperCase()}</div>
            <div>
              <div className="rp-strong" style={{ fontSize: 18 }}>{name}</div>
              <div className="rp-sub" style={{ fontSize: 13 }}>{meta}</div>
            </div>
          </div>
        </div>
      )}

      <div className="rp-split">
        {/* Calendar */}
        <div className="rp-card rp-card-pad">
          <div className="rp-cal-head">
            <button className="rp-chip" onClick={() => changeMonth(-1)} aria-label="Previous month">
              <ChevronLeft size={16} />
            </button>
            <div className="rp-cal-title">{monthLabel(month)}</div>
            <button
              className="rp-chip"
              disabled={month >= currentMonth}
              style={month >= currentMonth ? { opacity: 0.35 } : undefined}
              onClick={() => changeMonth(1)}
              aria-label="Next month"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="rp-cal-grid">
            {WEEKDAYS.map((d) => (
              <div key={d} className="rp-cal-wd">{d}</div>
            ))}
            {cells.map((c, i) => {
              if (!c) return <div key={`b-${i}`} className="rp-cal-day blank" />;
              const m = c.record ? statusMeta(c.record.status) : null;
              return (
                <button
                  key={c.date}
                  type="button"
                  className={`rp-cal-day ${c.date === todayKey ? 'today' : ''} ${c.date === selected ? 'selected' : ''}`}
                  style={m ? { background: m.bg } : undefined}
                  onClick={() => setSelected(c.date)}
                >
                  {c.day}
                  {m && <small style={{ color: m.color }}>{m.letter}</small>}
                </button>
              );
            })}
          </div>

          <div className="rp-legend">
            {Object.values(STATUS_META).map((m) => (
              <span key={m.label}>
                <i style={{ background: m.bg, color: m.color }}>{m.letter}</i>
                {m.label}
              </span>
            ))}
          </div>
        </div>

        {/* Right column: day details + summary */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="rp-card rp-card-pad">
            <div className="rp-toolbar" style={{ marginBottom: 12 }}>
              <h3 className="rp-card-title" style={{ margin: 0 }}>
                {inMonth ? formatFullDate(selected) : 'Attendance Details'}
              </h3>
              <div className="rp-spacer" />
              {recMeta && (
                <span className="rp-pill" style={{ background: recMeta.bg, color: recMeta.color }}>
                  {recMeta.label.toUpperCase()}
                </span>
              )}
            </div>

            {rec ? (
              <>
                <div className="rp-timegrid">
                  <div className="rp-timebox">
                    <div className="rp-field-label">Check In</div>
                    <div className="rp-field-value rp-strong">
                      {rec.checkInTime ? formatISTTime(rec.checkInTime) : '—'}
                    </div>
                  </div>
                  <div className="rp-timebox">
                    <div className="rp-field-label">Check Out</div>
                    <div className="rp-field-value rp-strong">
                      {rec.checkOutTime ? formatISTTime(rec.checkOutTime) : '—'}
                    </div>
                  </div>
                  <div className="rp-timebox">
                    <div className="rp-field-label">Working</div>
                    <div className="rp-field-value rp-strong">
                      {rec.workDurationMinutes ? formatDuration(rec.workDurationMinutes) : '—'}
                    </div>
                  </div>
                </div>

                {[['Check-In location', inLoc, '#10b981'], ['Check-Out location', outLoc, '#ef4444']].map(
                  ([label, l, color]) =>
                    l && (
                      <div key={label} className="rp-sumline" style={{ marginTop: 8 }}>
                        <span>
                          <MapPin size={13} color={color} style={{ verticalAlign: -2 }} /> {label}
                        </span>
                        <a
                          className="rp-link"
                          href={`https://www.google.com/maps?q=${l.lat},${l.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {l.text}
                        </a>
                      </div>
                    )
                )}
                {rec.remarks && (
                  <div className="rp-sub" style={{ marginTop: 10 }}>
                    Remarks: {String(rec.remarks).replace(/^\s*\|\s*/, '')}
                  </div>
                )}
              </>
            ) : (
              <div className="rp-muted" style={{ fontSize: 13 }}>
                {inMonth ? 'No attendance record for this day.' : 'Click a day in the calendar to see its details.'}
              </div>
            )}
          </div>

          <div className="rp-card rp-card-pad">
            <h3 className="rp-card-title">Monthly Summary</h3>
            {monthlyLoading && !data ? (
              <div className="rp-loading">Loading…</div>
            ) : (
              <div className="rp-ring-row">
                <Ring value={pct} />
                <div style={{ flex: 1, minWidth: 150 }}>
                  <div className="rp-sumline"><span>Working Days</span><b>{summary.workingDays || 0}</b></div>
                  <div className="rp-sumline"><span>Full Day</span><b>{summary.present || 0}</b></div>
                  <div className="rp-sumline"><span>Half Day</span><b>{summary.halfDay || 0}</b></div>
                  <div className="rp-sumline"><span>Absent</span><b>{summary.absent || 0}</b></div>
                  <div className="rp-sumline"><span>Leave</span><b>{summary.onLeave || 0}</b></div>
                  <div className="rp-sumline"><span>Week Off</span><b>{summary.weekOff || 0}</b></div>
                  <div className="rp-sumline"><span>Holiday</span><b>{summary.holiday || 0}</b></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeAttendancePage;


//---------- 13.10.26 Backup --------------
// // src/pages/Attendance/EmployeeAttendancePage.js
// // One employee's month: calendar, selected-day details, summary ring.
// import React, { useEffect, useMemo, useState } from 'react';
// import { useDispatch, useSelector } from 'react-redux';
// import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
// import { ChevronLeft, ChevronRight, MapPin, ArrowLeft } from 'lucide-react';
// import { fetchMonthlyAttendance } from '../../services/features/attendance/attendanceSlice';
// import {
//   WEEKDAYS,
//   STATUS_META,
//   statusMeta,
//   addMonths,
//   formatDateKey,
//   formatDuration,
//   formatFullDate,
//   formatISTTime,
//   monthLabel,
// } from '../../utils/attendanceFormat';
// import '../Shared/ReportPages.css';

// const coord = (loc) =>
//   loc && typeof loc.latitude === 'number'
//     ? { text: `${loc.latitude.toFixed(4)}, ${loc.longitude.toFixed(4)}`, lat: loc.latitude, lng: loc.longitude }
//     : null;

// const Ring = ({ value }) => {
//   const size = 120;
//   const r = 46;
//   const c = 2 * Math.PI * r;
//   const dash = (Math.min(100, Number(value) || 0) / 100) * c;
//   return (
//     <svg width={size} height={size} viewBox="0 0 120 120">
//       <circle cx="60" cy="60" r={r} fill="none" stroke="var(--border)" strokeWidth="12" />
//       <circle
//         cx="60" cy="60" r={r} fill="none" stroke="#10b981" strokeWidth="12" strokeLinecap="round"
//         strokeDasharray={`${dash} ${c - dash}`} transform="rotate(-90 60 60)"
//       />
//       <text x="60" y="65" textAnchor="middle" fontSize="18" fontWeight="800" fill="var(--text-primary)">
//         {Number(value || 0).toFixed(2)}%
//       </text>
//     </svg>
//   );
// };

// const EmployeeAttendancePage = () => {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const { employeeId } = useParams();
//   const [sp] = useSearchParams();
//   const { monthly, monthlyLoading } = useSelector((s) => s.attendance);

//   const todayKey = formatDateKey();
//   const currentMonth = todayKey.slice(0, 7);
//   const empId = employeeId === 'me' ? undefined : employeeId;

//   const [month, setMonth] = useState(sp.get('month') || currentMonth);
//   const [selected, setSelected] = useState(sp.get('date') || todayKey);

//   useEffect(() => {
//     dispatch(fetchMonthlyAttendance({ employeeId: empId, month }));
//   }, [dispatch, empId, month]);

//   // Only trust data that belongs to the month on screen
//   const data = monthly?.month === month ? monthly : null;

//   const byDate = useMemo(() => {
//     const m = {};
//     (data?.items || []).forEach((it) => (m[it.date] = it));
//     return m;
//   }, [data]);

//   const cells = useMemo(() => {
//     const [y, m] = month.split('-').map(Number);
//     const first = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
//     const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
//     const arr = Array.from({ length: first }, () => null);
//     for (let d = 1; d <= days; d++) {
//       const key = `${month}-${String(d).padStart(2, '0')}`;
//       arr.push({ day: d, date: key, record: byDate[key] });
//     }
//     return arr;
//   }, [month, byDate]);

//   const summary = data?.summary || {};
//   const pct = Number(data?.attendancePct) || 0;

//   const changeMonth = (delta) => {
//     setMonth((m) => addMonths(m, delta));
//     setSelected(null);
//   };

//   const inMonth = selected && selected.startsWith(month);
//   const rec = inMonth ? byDate[selected] : null;
//   const recMeta = rec ? statusMeta(rec.status) : null;
//   const inLoc = rec ? coord(rec.checkInLocation) : null;
//   const outLoc = rec ? coord(rec.checkOutLocation) : null;

//   const name = sp.get('name') || 'Employee';
//   const meta = [sp.get('role'), sp.get('mobile')].filter(Boolean).join(' · ');

//   return (
//     <div className="rp-page">
//       <div className="rp-toolbar">
//         <button className="rp-chip" onClick={() => navigate('/attendance')}>
//           <ArrowLeft size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
//           Attendance Dashboard
//         </button>
//       </div>

//       {/* Employee header */}
//       {empId && (
//         <div className="rp-card rp-card-pad">
//           <div className="rp-person" style={{ gap: 14 }}>
//             <div className="rp-avatar lg">{name.charAt(0).toUpperCase()}</div>
//             <div>
//               <div className="rp-strong" style={{ fontSize: 18 }}>{name}</div>
//               <div className="rp-sub" style={{ fontSize: 13 }}>{meta}</div>
//             </div>
//           </div>
//         </div>
//       )}

//       <div className="rp-split">
//         {/* Calendar */}
//         <div className="rp-card rp-card-pad">
//           <div className="rp-cal-head">
//             <button className="rp-chip" onClick={() => changeMonth(-1)} aria-label="Previous month">
//               <ChevronLeft size={16} />
//             </button>
//             <div className="rp-cal-title">{monthLabel(month)}</div>
//             <button
//               className="rp-chip"
//               disabled={month >= currentMonth}
//               style={month >= currentMonth ? { opacity: 0.35 } : undefined}
//               onClick={() => changeMonth(1)}
//               aria-label="Next month"
//             >
//               <ChevronRight size={16} />
//             </button>
//           </div>

//           <div className="rp-cal-grid">
//             {WEEKDAYS.map((d) => (
//               <div key={d} className="rp-cal-wd">{d}</div>
//             ))}
//             {cells.map((c, i) => {
//               if (!c) return <div key={`b-${i}`} className="rp-cal-day blank" />;
//               const m = c.record ? statusMeta(c.record.status) : null;
//               return (
//                 <button
//                   key={c.date}
//                   type="button"
//                   className={`rp-cal-day ${c.date === todayKey ? 'today' : ''} ${c.date === selected ? 'selected' : ''}`}
//                   style={m ? { background: m.bg } : undefined}
//                   onClick={() => setSelected(c.date)}
//                 >
//                   {c.day}
//                   {m && <small style={{ color: m.color }}>{m.letter}</small>}
//                 </button>
//               );
//             })}
//           </div>

//           <div className="rp-legend">
//             {Object.values(STATUS_META).map((m) => (
//               <span key={m.label}>
//                 <i style={{ background: m.bg, color: m.color }}>{m.letter}</i>
//                 {m.label}
//               </span>
//             ))}
//           </div>
//         </div>

//         {/* Right column: day details + summary */}
//         <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
//           <div className="rp-card rp-card-pad">
//             <div className="rp-toolbar" style={{ marginBottom: 12 }}>
//               <h3 className="rp-card-title" style={{ margin: 0 }}>
//                 {inMonth ? formatFullDate(selected) : 'Attendance Details'}
//               </h3>
//               <div className="rp-spacer" />
//               {recMeta && (
//                 <span className="rp-pill" style={{ background: recMeta.bg, color: recMeta.color }}>
//                   {recMeta.label.toUpperCase()}
//                 </span>
//               )}
//             </div>

//             {rec ? (
//               <>
//                 <div className="rp-timegrid">
//                   <div className="rp-timebox">
//                     <div className="rp-field-label">Check In</div>
//                     <div className="rp-field-value rp-strong">
//                       {rec.checkInTime ? formatISTTime(rec.checkInTime) : '—'}
//                     </div>
//                   </div>
//                   <div className="rp-timebox">
//                     <div className="rp-field-label">Check Out</div>
//                     <div className="rp-field-value rp-strong">
//                       {rec.checkOutTime ? formatISTTime(rec.checkOutTime) : '—'}
//                     </div>
//                   </div>
//                   <div className="rp-timebox">
//                     <div className="rp-field-label">Working</div>
//                     <div className="rp-field-value rp-strong">
//                       {rec.workDurationMinutes ? formatDuration(rec.workDurationMinutes) : '—'}
//                     </div>
//                   </div>
//                 </div>

//                 {[['Check-In location', inLoc, '#10b981'], ['Check-Out location', outLoc, '#ef4444']].map(
//                   ([label, l, color]) =>
//                     l && (
//                       <div key={label} className="rp-sumline" style={{ marginTop: 8 }}>
//                         <span>
//                           <MapPin size={13} color={color} style={{ verticalAlign: -2 }} /> {label}
//                         </span>
//                         <a
//                           className="rp-link"
//                           href={`https://www.google.com/maps?q=${l.lat},${l.lng}`}
//                           target="_blank"
//                           rel="noopener noreferrer"
//                         >
//                           {l.text}
//                         </a>
//                       </div>
//                     )
//                 )}
//                 {rec.remarks && (
//                   <div className="rp-sub" style={{ marginTop: 10 }}>
//                     Remarks: {String(rec.remarks).replace(/^\s*\|\s*/, '')}
//                   </div>
//                 )}
//               </>
//             ) : (
//               <div className="rp-muted" style={{ fontSize: 13 }}>
//                 {inMonth ? 'No attendance record for this day.' : 'Click a day in the calendar to see its details.'}
//               </div>
//             )}
//           </div>

//           <div className="rp-card rp-card-pad">
//             <h3 className="rp-card-title">Monthly Summary</h3>
//             {monthlyLoading && !data ? (
//               <div className="rp-loading">Loading…</div>
//             ) : (
//               <div className="rp-ring-row">
//                 <Ring value={pct} />
//                 <div style={{ flex: 1, minWidth: 150 }}>
//                   <div className="rp-sumline"><span>Working Days</span><b>{summary.workingDays || 0}</b></div>
//                   <div className="rp-sumline"><span>Full Day</span><b>{summary.present || 0}</b></div>
//                   <div className="rp-sumline"><span>Half Day</span><b>{summary.halfDay || 0}</b></div>
//                   <div className="rp-sumline"><span>Absent</span><b>{summary.absent || 0}</b></div>
//                   <div className="rp-sumline"><span>Leave</span><b>{summary.onLeave || 0}</b></div>
//                   <div className="rp-sumline"><span>Week Off</span><b>{summary.weekOff || 0}</b></div>
//                   <div className="rp-sumline"><span>Holiday</span><b>{summary.holiday || 0}</b></div>
//                 </div>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default EmployeeAttendancePage;
